import { api } from "./client";
import { apiRoutes } from "util/apiRoutes";
import { MarkerType, type Edge, type Node } from "reactflow";
import type { GraphIssueNodeData } from "components/Graph/GraphIssueNodeTypes";
import type { Issue } from "util/constants";

export interface GraphEdgeNotePlace {
  id: number;
  name?: string | null;
  formatted_address?: string | null;
}

export interface GraphEdgeNoteProperties extends Record<string, unknown> {
  timeline_ts?: string | null;
  place_id?: number | null;
  place?: GraphEdgeNotePlace | null;
}

export interface GraphEdgeNoteSummary {
  id: number;
  user_id?: number | null;
  username?: string | null;
  collection_id?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
  content: string;
  properties?: GraphEdgeNoteProperties;
}

/**
 * Counts surfaced as edge badges in the graph canvas. All values are
 * non-negative integers; absent fields default to 0 at render time.
 */
export interface GraphEdgeBadgeCounts {
  otherCollections: number;
  notes: number;
  impacts: number;
  solutions: number;
  actions: number;
}

export interface GraphSourceDescriptor {
  collectionId: number;
  title: string | null;
  actorName: string | null;
  actorKind: "user" | "anonymous";
  graphMode: string | null;
  role: "self" | "source";
  syncEnabled?: boolean;
}

export interface GraphCanvasEdgeData {
  relationId?: number | null;
  note?: GraphEdgeNoteSummary | null;
  notes?: GraphEdgeNoteSummary[];
  badges?: GraphEdgeBadgeCounts;
  mergedFrom?: string[];
  /**
   * Local-only temporary flag used while an optimistic on-canvas connect is
   * in flight before the server returns the real compound issue id.
   */
  pending?: boolean;
}

/**
 * Shape of a collection post returned by the graph feed endpoint. The backend
 * returns full post rows (see `listGraphCollectionPosts` in
 * `graphCollectionService.js`), so we keep the type open-ended but document the
 * headline fields that callers rely on. Extra fields (body, url, privacy, vote
 * totals, viewer_relation, ...) are passed through to `EntityCard`.
 */
export interface GraphFeedPost extends Record<string, unknown> {
  id: number;
  title?: string | null;
  subtitle?: string | null;
  body?: string | null;
  url?: string | null;
  user_id?: number | string | null;
  created_at?: string | null;
  updated_at?: string | null;
  properties?: Record<string, unknown>;
  viewer_relation?: string | null;
}

export interface GraphFeedSnapshotItem {
  post: GraphFeedPost;
  graph: GraphCollectionSnapshot | null;
}

export interface GraphCollectionSnapshot {
  collection?: (GraphFeedPost & Record<string, unknown>) | null;
  issues: Array<Record<string, unknown>>;
  edges: Array<{
    relationId?: number | null;
    sourceId: number;
    targetId: number;
    note?: GraphEdgeNoteSummary | null;
    notes?: GraphEdgeNoteSummary[];
    badges?: GraphEdgeBadgeCounts;
    mergedFrom?: string[];
  }>;
  positions_by_issue_id: Record<string, { x: number; y: number }>;
  item_relations?: Array<{
    relation_id: string;
    related_id: number;
    related_type: string;
    position?: { x: number; y: number } | null;
    merged_from?: string;
  }>;
  sync?: { source_collection_id?: number; sync_enabled?: boolean } | null;
  sources?: GraphSourceDescriptor[];
}

export interface GraphCollectionsFeedResponse {
  items: GraphFeedSnapshotItem[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

const buildFallbackGraphEdgeId = (sourceId: number, targetId: number): string =>
  `${sourceId}->${targetId}`;

export const buildGraphRelationEdgeId = (relationId: number | string): string =>
  `issue-${String(relationId)}`;

/**
 * Returns a trimmed, truthy string value or null. A string that's just the raw
 * numeric ID ("124") is rejected because it means the snapshot row is missing
 * its real name/cname — falling back to the ID would surface an opaque number
 * in the UI instead of a readable label.
 */
const toDisplayString = (value: unknown, rawId: number | null): string | null => {
  if (typeof value !== "string" && typeof value !== "number") {
    return null;
  }
  const normalized = String(value).trim();
  if (normalized.length === 0) {
    return null;
  }
  if (rawId !== null && Number.isFinite(rawId) && normalized === String(rawId)) {
    return null;
  }
  return normalized;
};

const normalizeIssueSnapshotEntity = (issue: Record<string, unknown>): Issue => {
  const attrs =
    issue.attributes && typeof issue.attributes === "object"
      ? (issue.attributes as Record<string, unknown>)
      : issue;
  const rawId = Number((attrs.id ?? issue.id) as number | string);
  const idForFilter = Number.isFinite(rawId) ? rawId : null;
  const resolvedCname =
    toDisplayString(attrs.cname, idForFilter) ??
    toDisplayString(issue.cname, idForFilter) ??
    toDisplayString(attrs.subject_cname, idForFilter) ??
    toDisplayString(issue.subject_cname, idForFilter) ??
    "";
  const resolvedName =
    toDisplayString(attrs.name, idForFilter) ??
    toDisplayString(issue.name, idForFilter) ??
    (resolvedCname ? resolvedCname.replace(/_/g, " ") : "");
  const properties =
    (attrs.properties as Record<string, unknown> | undefined) ??
    (issue.properties as Record<string, unknown> | undefined) ??
    { subject_properties: {} };

  if (!resolvedCname && !resolvedName) {
    // eslint-disable-next-line no-console -- surface incomplete snapshot rows in dev so they can be debugged.
    console.warn(
      "[graphCollections] Snapshot issue row is missing both name and cname; rendering with ID fallback",
      { id: rawId }
    );
  }

  return {
    ...(attrs as Partial<Issue>),
    ...(issue as Partial<Issue>),
    id: rawId,
    name: resolvedName,
    cname: resolvedCname,
    properties: properties as Issue["properties"],
  } as Issue;
};

export interface GraphRebrewPayload {
  /**
   * Preferred shape (array of 1+ source collection ids). For single-source
   * copy/sync modes, a single-element array is required.
   */
  sourceCollectionIds?: number[];
  /**
   * Legacy single-source form; still accepted by the backend but prefer
   * `sourceCollectionIds`.
   * @deprecated
   */
  sourceCollectionId?: number;
  mode: "copy" | "sync" | "remix";
  title?: string | null;
  privacy?: "public" | "anonymous" | "followers" | "private" | "me" | null;
}

export interface GraphConnectPayload {
  influencerIssueId: number;
  influencedIssueId: number;
  position?: { x: number; y: number } | null;
}

export interface GraphConnectResponse {
  compoundIssueId: number;
  relation: Record<string, unknown> | null;
}

export interface GraphDetachResponse {
  removedRelationId: string | number | null;
  deactivatedCommentIds: number[];
  undoToken?: {
    collectionId: number;
    compoundIssueId: number;
    deactivatedCommentIds: number[];
    issuedAt: number;
    ttlMs: number;
  };
}

export interface GraphRestoreInfluenceResponse {
  relation: Record<string, unknown> | null;
  reactivatedCommentIds: number[];
}

export interface GraphRemoveIssueResponse {
  removedRelationId: string | number | null;
}

export interface GraphRestoreIssueResponse {
  relation: Record<string, unknown> | null;
}

export interface GraphCollectionSearchResponse {
  items: GraphFeedSnapshotItem[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  query: string;
}

export interface GraphCollectionEdgeNotesResponse {
  items: Array<Record<string, unknown>>;
  limit: number;
  offset: number;
}

/**
 * Shared TanStack Query key factory for graph collection state. Using a
 * single, structured factory keeps invalidation cheap: every snapshot-
 * invalidating mutation calls `queryClient.invalidateQueries(graphCollectionKeys.snapshot(id))`
 * without stringly-typed tags.
 */
export const graphCollectionKeys = {
  all: () => ["graphCollections"] as const,
  feed: () => [...graphCollectionKeys.all(), "feed"] as const,
  search: (query: string) => [...graphCollectionKeys.all(), "search", query] as const,
  snapshot: (collectionId: number) =>
    [...graphCollectionKeys.all(), "snapshot", collectionId] as const,
  edgeNotes: (collectionId: number, compoundIssueId: number) =>
    [
      ...graphCollectionKeys.all(),
      "edgeNotes",
      collectionId,
      compoundIssueId,
    ] as const,
};

export const graphCollectionsApi = {
  getFeed: async (params?: { limit?: number; offset?: number }): Promise<GraphCollectionsFeedResponse> => {
    const response = await api.get<GraphCollectionsFeedResponse>(apiRoutes.graph.collectionsFeed, {
      params,
    });
    return response.data;
  },

  search: async (params: { query: string; limit?: number; offset?: number }): Promise<GraphCollectionSearchResponse> => {
    const response = await api.get<GraphCollectionSearchResponse>(apiRoutes.graph.collectionsSearch, {
      params: {
        q: params.query,
        limit: params.limit,
        offset: params.offset,
      },
    });
    return response.data;
  },

  getSnapshot: async (collectionId: number): Promise<GraphCollectionSnapshot> => {
    const response = await api.get<GraphCollectionSnapshot>(
      apiRoutes.graph.collectionSnapshot(collectionId)
    );
    return response.data;
  },

  rebrew: async (payload: GraphRebrewPayload): Promise<{ collection: Record<string, unknown>; mode: string }> => {
    const response = await api.post<{ collection: Record<string, unknown>; mode: string }>(
      apiRoutes.graph.collectionsRebrew,
      payload
    );
    return response.data;
  },

  /**
   * One-shot on-canvas connect: create (or reuse) the compound issue between
   * two visible issues and attach it to the collection. Idempotent across
   * retries.
   */
  connectIssues: async (
    collectionId: number,
    payload: GraphConnectPayload
  ): Promise<GraphConnectResponse> => {
    const response = await api.post<GraphConnectResponse>(
      apiRoutes.graph.collectionInfluences(collectionId),
      payload
    );
    return response.data;
  },

  /**
   * Detach an influence from a graph collection. Soft-deletes the caller's
   * collection-scoped edge notes as part of the same transaction and returns
   * an undo token.
   */
  detachInfluence: async (
    collectionId: number,
    compoundIssueId: number
  ): Promise<GraphDetachResponse> => {
    const response = await api.delete<GraphDetachResponse>(
      apiRoutes.graph.collectionInfluenceItem(collectionId, compoundIssueId)
    );
    return response.data;
  },

  /**
   * Undo-partner for detachInfluence. Re-activates the relation and the
   * caller-owned comments listed in `deactivatedCommentIds`.
   */
  restoreInfluence: async (
    collectionId: number,
    compoundIssueId: number,
    payload?: { deactivatedCommentIds?: number[] }
  ): Promise<GraphRestoreInfluenceResponse> => {
    const response = await api.post<GraphRestoreInfluenceResponse>(
      apiRoutes.graph.collectionInfluenceRestore(collectionId, compoundIssueId),
      payload ?? {}
    );
    return response.data;
  },

  removeIssue: async (
    collectionId: number,
    issueId: number
  ): Promise<GraphRemoveIssueResponse> => {
    const response = await api.delete<GraphRemoveIssueResponse>(
      apiRoutes.graph.collectionIssueItem(collectionId, issueId)
    );
    return response.data;
  },

  restoreIssue: async (
    collectionId: number,
    issueId: number,
    payload?: { position?: { x: number; y: number } | null }
  ): Promise<GraphRestoreIssueResponse> => {
    const response = await api.post<GraphRestoreIssueResponse>(
      apiRoutes.graph.collectionIssueRestore(collectionId, issueId),
      payload ?? {}
    );
    return response.data;
  },

  listEdgeNotes: async (
    collectionId: number,
    compoundIssueId: number,
    params?: { limit?: number; offset?: number }
  ): Promise<GraphCollectionEdgeNotesResponse> => {
    const response = await api.get<GraphCollectionEdgeNotesResponse>(
      apiRoutes.graph.collectionEdgeNotes(collectionId, compoundIssueId),
      { params }
    );
    return response.data;
  },
};

/**
 * Extracts the root/starting issue id the graph was seeded from. Tries the
 * canonical `seed_issue_id` property first; falls back to the first laid-out
 * issue so callers always get a deterministic anchor for the recenter action
 * even when older snapshots omit the seed property.
 */
const resolveSnapshotRootIssueId = (
  snapshot: GraphCollectionSnapshot | null | undefined,
  mappedNodeIds: string[]
): string | null => {
  const rawProperties = snapshot?.collection?.properties;
  if (rawProperties && typeof rawProperties === "object") {
    const seedValue = (rawProperties as Record<string, unknown>).seed_issue_id;
    const parsed = Number(seedValue);
    if (Number.isFinite(parsed) && parsed > 0) {
      return String(parsed);
    }
  }
  return mappedNodeIds.length > 0 ? mappedNodeIds[0] : null;
};

export interface GraphSnapshotCanvasData {
  nodes: Array<Node<GraphIssueNodeData>>;
  edges: Array<Edge<GraphCanvasEdgeData>>;
  /**
   * React Flow node id of the seed/root issue, if known. Downstream surfaces
   * use this as the recenter target so the "Recenter" control returns the
   * user to the graph's starting node rather than the origin (0, 0) which
   * may be empty for user-positioned graphs.
   */
  rootNodeId: string | null;
}

/**
 * Maps one graph snapshot payload to React Flow nodes/edges and surfaces the
 * seed issue id so consumers can drive root-aware controls (recenter, etc.).
 */
export function mapGraphSnapshotToCanvasData(
  snapshot: GraphCollectionSnapshot | null | undefined
): GraphSnapshotCanvasData {
  const issues = snapshot?.issues ?? [];
  const positions = snapshot?.positions_by_issue_id ?? {};
  const edgesRaw = snapshot?.edges ?? [];

  const nodes: Array<Node<GraphIssueNodeData>> = issues.map((issue) => {
    const entity = normalizeIssueSnapshotEntity(issue);
    const id = Number(entity.id);
    const pos =
      (positions as Record<string, { x: number; y: number }>)[String(id)] ??
      (positions as Record<number, { x: number; y: number }>)[id];
    const position = pos && typeof pos === "object" && "x" in pos && "y" in pos
      ? { x: Number(pos.x), y: Number(pos.y) }
      : { x: 0, y: 0 };
    return {
      id: String(id),
      type: "issueNode",
      position,
      data: {
        entity,
        additionalInfluencersCount: 0,
        additionalInfluencedCount: 0,
        totalInfluencers: 0,
        totalInfluenced: 0,
        onExpandInfluencers: () => undefined,
        onExpandInfluenced: () => undefined,
      },
    };
  });

  const edges: Array<Edge<GraphCanvasEdgeData>> = edgesRaw.map((e) => {
    const relationId = Number.isFinite(Number(e.relationId)) ? Number(e.relationId) : null;
    const badges: GraphEdgeBadgeCounts = e.badges
      ? {
          otherCollections: Number(e.badges.otherCollections || 0),
          notes: Number(e.badges.notes || 0),
          impacts: Number(e.badges.impacts || 0),
          solutions: Number(e.badges.solutions || 0),
          actions: Number(e.badges.actions || 0),
        }
      : {
          otherCollections: 0,
          notes: e.notes?.length || (e.note ? 1 : 0),
          impacts: 0,
          solutions: 0,
          actions: 0,
        };
    return {
      id: relationId ? buildGraphRelationEdgeId(relationId) : buildFallbackGraphEdgeId(e.sourceId, e.targetId),
      source: String(e.sourceId),
      target: String(e.targetId),
      type: "influenceEdge",
      markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16 },
      data: {
        relationId,
        note: e.note ?? null,
        notes: Array.isArray(e.notes) ? e.notes : (e.note ? [e.note] : []),
        badges,
        mergedFrom: Array.isArray(e.mergedFrom) ? e.mergedFrom : [],
      },
    };
  });

  const rootNodeId = resolveSnapshotRootIssueId(
    snapshot,
    nodes.map((node) => node.id)
  );

  return { nodes, edges, rootNodeId };
}

export interface GraphCollectionFeedEntry {
  /** The full collection post row (used as the EntityCard entity). */
  post: GraphFeedPost;
  /** React Flow nodes derived from the snapshot. */
  nodes: Array<Node<GraphIssueNodeData>>;
  /** React Flow edges derived from the snapshot. */
  edges: Array<Edge<GraphCanvasEdgeData>>;
  /** Seed/root node id for recenter actions. */
  rootNodeId: string | null;
}

/**
 * Maps API feed items to presentation-ready entries that preserve the full
 * post payload so consumers can render them with EntityCard (vote/save/report
 * actions, comments, edit, etc.) while still exposing the graph snapshot as a
 * dedicated render block above the media area.
 */
export function mapFeedToGraphCollectionItems(
  feedItems: GraphFeedSnapshotItem[]
): GraphCollectionFeedEntry[] {
  return feedItems.map((entry) => {
    const { nodes, edges, rootNodeId } = mapGraphSnapshotToCanvasData(entry.graph);
    return {
      post: entry.post,
      nodes,
      edges,
      rootNodeId,
    };
  });
}
